import { createFileRoute, Link } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  usePlaidLink,
  type PlaidLinkOnExitMetadata,
  type PlaidLinkError,
  type PlaidLinkOnSuccessMetadata,
} from 'react-plaid-link';
import {
  BANK_ACCOUNT_TYPES,
  MANUAL_TX_DIRECTIONS,
  type BankAccount,
  type BankAccountType,
  type CreateManualBankAccountInput,
  type CreateMockBankAccountInput,
  type ManualTxDirection,
  type UpdateBankAccountInput,
} from '@accounting/shared';
import {
  useAddManualTransaction,
  useBankAccounts,
  useCreateManualBankAccount,
  useCreateMockBankAccount,
  useCreatePlaidLinkToken,
  useDeactivateBankAccount,
  useExchangePlaidToken,
  useLinkAdditionalPlaidAccount,
  usePlaidItemAccounts,
  useCreatePlaidUpdateLinkToken,
  useResyncBankAccount,
  useSeedMockTransactions,
  useSwapPlaidAccount,
  useSyncBankAccount,
  useUpdateBankAccount,
  type PlaidItemAccountStatus,
} from '../../hooks/useBank';
import { useAccounts } from '../../hooks/useAccounts';
import { AccountPicker } from '../../components/accounts/AccountPicker';
import { StatementsPanel } from '../../components/statements/StatementsPanel';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { ApiError } from '../../lib/api';
import { formatAccountingDate, humanizeLabel, todayIso } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/bank/')({
  component: BankPage,
});

// Holds the Plaid Link state across the OAuth round-trip (the major-US-bank
// flow navigates the whole tab to the bank's auth page; on return Plaid sticks
// ?oauth_state_id=... on the URL and we resume Link with the SAME link_token).
const PLAID_RESUME_KEY = 'plaid_link_resume';

function BankPage() {
  const companyId = useActiveCompanyId();
  const { data, isLoading, error } = useBankAccounts(companyId);
  const { data: accounts = [] } = useAccounts(companyId);
  const accountById = new Map(accounts.map((a) => [a.id, a]));
  const [showCreate, setShowCreate] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const oauthStateId = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('oauth_state_id')
    : null;

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Bank accounts</h1>
        <p className="muted">No active company.</p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
          Go to Companies
        </Link>
      </div>
    );
  }

  const bankAccounts = data?.bankAccounts ?? [];
  const plaidEnv = data?.plaidEnv ?? 'mock';

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Bank accounts</h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
            Plaid env: <strong>{plaidEnv}</strong>
            {plaidEnv === 'mock' && ' — using stub data, no real bank involved'}
          </p>
        </div>
        {!showCreate && !showManual && !showConnect && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to="/bank/rules" className="btn-secondary" style={{ textDecoration: 'none' }}>
              Manage rules
            </Link>
            <Link to="/bank/transactions" className="btn-secondary" style={{ textDecoration: 'none' }}>
              View transactions
            </Link>
            <button className="btn-secondary" onClick={() => setShowManual(true)}>
              + Manual account
            </button>
            {plaidEnv === 'mock' ? (
              <button className="btn-primary" onClick={() => setShowCreate(true)}>
                + Mock bank account
              </button>
            ) : (
              <button className="btn-primary" onClick={() => setShowConnect(true)}>
                + Connect bank
              </button>
            )}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateMockBankForm
          companyId={companyId}
          onClose={() => setShowCreate(false)}
        />
      )}

      {showManual && (
        <CreateManualBankForm
          companyId={companyId}
          onClose={() => setShowManual(false)}
        />
      )}

      {showConnect && (
        <ConnectPlaidForm companyId={companyId} onClose={() => setShowConnect(false)} />
      )}

      {oauthStateId && <PlaidOAuthResume companyId={companyId} />}

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load bank accounts.</p>}

      {!isLoading && bankAccounts.length === 0 && !showCreate && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>
            No bank accounts yet.
          </p>
          {plaidEnv === 'mock' && (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              Create a mock bank account
            </button>
          )}
        </div>
      )}

      {bankAccounts.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Account</th>
              <th>GL account</th>
              <th>Type</th>
              <th>Last synced</th>
              <th>Status</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {bankAccounts.map((b) => (
              <BankAccountRow
                key={b.id}
                bank={b}
                companyId={companyId}
                plaidEnv={plaidEnv}
                glAccount={accountById.get(b.accountId)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function BankAccountRow({
  bank,
  companyId,
  plaidEnv,
  glAccount,
}: {
  bank: BankAccount;
  companyId: string;
  plaidEnv: 'mock' | 'sandbox' | 'production';
  glAccount?: { code: string; name: string };
}) {
  const sync = useSyncBankAccount(companyId);
  const resync = useResyncBankAccount(companyId);
  const deactivate = useDeactivateBankAccount(companyId);
  const [showSeed, setShowSeed] = useState(false);
  const [showAddTx, setShowAddTx] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showStatements, setShowStatements] = useState(false);
  const [showPlaidItem, setShowPlaidItem] = useState(false);
  const isPlaidBacked = !bank.isMock && !bank.isManual;

  return (
    <>
      <tr style={{ opacity: bank.isActive ? 1 : 0.6 }}>
        <td>
          <div style={{ fontWeight: 500 }}>{bank.accountName}</div>
          <div className="muted" style={{ fontSize: 12 }}>
            {bank.institutionName ?? '—'}
            {bank.accountMask && ` ····${bank.accountMask}`}
          </div>
        </td>
        <td>
          {glAccount ? (
            <span style={{ fontSize: 13 }}>
              <span style={{ fontFamily: 'ui-monospace, monospace' }}>{glAccount.code}</span> ·{' '}
              {glAccount.name}
            </span>
          ) : (
            <span className="field-error" style={{ fontSize: 12 }}>missing</span>
          )}
        </td>
        <td>
          {humanizeLabel(bank.accountType)}
          {bank.isMock && <span className="tag warn" style={{ marginLeft: 6 }}>Mock</span>}
          {bank.isManual && <span className="tag" style={{ marginLeft: 6 }}>Manual</span>}
        </td>
        <td className="muted">
          {bank.lastSyncedAt
            ? new Date(bank.lastSyncedAt).toLocaleString()
            : 'never'}
          {bank.lastSyncError && (
            <div className="field-error" style={{ fontSize: 12, marginTop: 4 }}>
              {bank.lastSyncError}
            </div>
          )}
          {extractApiError(sync.error) && (
            <div className="field-error" style={{ fontSize: 12, marginTop: 4 }}>
              Sync: {extractApiError(sync.error)}
            </div>
          )}
          {extractApiError(resync.error) && (
            <div className="field-error" style={{ fontSize: 12, marginTop: 4 }}>
              Resync: {extractApiError(resync.error)}
            </div>
          )}
        </td>
        <td>
          {bank.isActive ? (
            <span className="tag ok">Active</span>
          ) : (
            <span className="tag bad">Inactive</span>
          )}
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          <button
            className="btn-secondary"
            onClick={() => setShowEdit((v) => !v)}
            style={{ marginRight: 6 }}
          >
            {showEdit ? 'Close' : 'Edit'}
          </button>
          {bank.isActive && (
            <>
              <button
                className="btn-secondary"
                onClick={() => sync.mutate(bank.id)}
                disabled={sync.isPending || resync.isPending}
                style={{ marginRight: 6 }}
              >
                {sync.isPending ? 'Syncing…' : 'Sync now'}
              </button>
              {isPlaidBacked && (
                <button
                  className="btn-secondary"
                  onClick={() => {
                    if (
                      confirm(
                        'Full resync will reset the Plaid cursor and re-pull every transaction from account inception. Existing transactions will not be duplicated. Continue?',
                      )
                    ) {
                      resync.mutate(bank.id);
                    }
                  }}
                  disabled={sync.isPending || resync.isPending}
                  style={{ marginRight: 6 }}
                  title="Resets the Plaid cursor and re-pulls the full history. Use if Sync fails or you need to backfill."
                >
                  {resync.isPending ? 'Resyncing…' : 'Full resync'}
                </button>
              )}
              {isPlaidBacked && needsUpdateMode(bank.lastSyncError) && (
                <FixConnectionButton
                  companyId={companyId}
                  bankAccountId={bank.id}
                />
              )}
              {plaidEnv === 'mock' && bank.isMock && (
                <button
                  className="btn-secondary"
                  onClick={() => setShowSeed((v) => !v)}
                  style={{ marginRight: 6 }}
                >
                  {showSeed ? 'Hide' : 'Seed transactions'}
                </button>
              )}
              {bank.isManual && (
                <button
                  className="btn-primary"
                  onClick={() => setShowAddTx((v) => !v)}
                  style={{ marginRight: 6 }}
                >
                  {showAddTx ? 'Hide' : '+ Add transaction'}
                </button>
              )}
              <button
                className="btn-secondary"
                onClick={() => setShowStatements((v) => !v)}
                style={{ marginRight: 6 }}
              >
                {showStatements ? 'Hide statements' : 'Statements'}
              </button>
              {isPlaidBacked && (
                <button
                  className="btn-secondary"
                  onClick={() => setShowPlaidItem((v) => !v)}
                  style={{ marginRight: 6 }}
                  title="See every Plaid account on this connection; swap, or link more."
                >
                  {showPlaidItem ? 'Hide Plaid accounts' : 'Plaid accounts'}
                </button>
              )}
              <button
                className="btn-secondary"
                onClick={() => {
                  if (confirm(`Deactivate "${bank.accountName}"?`)) {
                    deactivate.mutate(bank.id);
                  }
                }}
              >
                Deactivate
              </button>
            </>
          )}
        </td>
      </tr>
      {showEdit && (
        <tr>
          <td colSpan={6} style={{ background: 'var(--bg-surface)', padding: 12 }}>
            <EditBankAccountForm companyId={companyId} bank={bank} onClose={() => setShowEdit(false)} />
          </td>
        </tr>
      )}
      {sync.data && (
        <tr>
          <td colSpan={6} style={{ background: 'var(--bg-surface)', fontSize: 13 }}>
            Sync result: {sync.data.result.added} added, {sync.data.result.modified} modified,{' '}
            {sync.data.result.removed} removed · {sync.data.result.rulesMatched} rule
            {sync.data.result.rulesMatched === 1 ? '' : 's'} matched
            {sync.data.result.autoPosted > 0 && ` · ${sync.data.result.autoPosted} auto-posted`}
            {sync.data.result.suppressedByCeiling > 0 &&
              ` · ${sync.data.result.suppressedByCeiling} ceiling-suppressed`}
            {sync.data.result.suggested > 0 && ` · ${sync.data.result.suggested} suggested`}
          </td>
        </tr>
      )}
      {showSeed && (
        <tr>
          <td colSpan={6} style={{ background: 'var(--bg-surface)', padding: 12 }}>
            <SeedTransactionsForm
              companyId={companyId}
              bankAccountId={bank.id}
              onClose={() => setShowSeed(false)}
            />
          </td>
        </tr>
      )}
      {showAddTx && (
        <tr>
          <td colSpan={6} style={{ background: 'var(--bg-surface)', padding: 12 }}>
            <AddManualTransactionForm
              companyId={companyId}
              bankAccountId={bank.id}
              onClose={() => setShowAddTx(false)}
            />
          </td>
        </tr>
      )}
      {showStatements && (
        <tr>
          <td colSpan={6} style={{ background: 'var(--bg-surface)', padding: 12 }}>
            <StatementsPanel companyId={companyId} bankAccountId={bank.id} />
          </td>
        </tr>
      )}
      {showPlaidItem && (
        <tr>
          <td colSpan={6} style={{ background: 'var(--bg-surface)', padding: 12 }}>
            <PlaidItemAccountsPanel companyId={companyId} bankAccountId={bank.id} />
          </td>
        </tr>
      )}
    </>
  );
}

function PlaidItemAccountsPanel({
  companyId,
  bankAccountId,
}: {
  companyId: string;
  bankAccountId: string;
}) {
  const { data, isLoading, error } = usePlaidItemAccounts(companyId, bankAccountId);
  const swap = useSwapPlaidAccount(companyId);
  const linkMore = useLinkAdditionalPlaidAccount(companyId);
  const { data: accounts = [] } = useAccounts(companyId);

  // Per-row state for the "Link this one" GL picker.
  const [glChoice, setGlChoice] = useState<Record<string, string>>({});
  const [opError, setOpError] = useState<string | null>(null);

  if (isLoading) return <p className="muted">Loading accounts on this connection…</p>;
  if (error)
    return (
      <p className="field-error">
        Could not list accounts on this Plaid connection. Try Sync now first if you just
        connected.
      </p>
    );

  const items = data ?? [];

  const handleSwap = (target: PlaidItemAccountStatus) => {
    setOpError(null);
    swap.mutate(
      { bankAccountId, plaidAccountId: target.plaidAccountId },
      {
        onError: (err) =>
          setOpError(
            err instanceof ApiError
              ? (err.body as { error?: string } | null)?.error ?? 'Could not swap account'
              : err.message,
          ),
      },
    );
  };

  const handleLinkAdditional = (target: PlaidItemAccountStatus) => {
    const glId = glChoice[target.plaidAccountId];
    if (!glId) return;
    setOpError(null);
    linkMore.mutate(
      { sourceBankAccountId: bankAccountId, plaidAccountId: target.plaidAccountId, glAccountId: glId },
      {
        onSuccess: () => setGlChoice((p) => ({ ...p, [target.plaidAccountId]: '' })),
        onError: (err) =>
          setOpError(
            err instanceof ApiError
              ? (err.body as { error?: string } | null)?.error ?? 'Could not link'
              : err.message,
          ),
      },
    );
  };

  return (
    <div>
      <div style={{ fontWeight: 500, marginBottom: 4 }}>Plaid accounts on this connection</div>
      <p className="muted" style={{ marginTop: 0, fontSize: 12 }}>
        Every account at your bank that Plaid returned for this Item. Swap which one this row
        points to, or link additional ones to their own GL accounts. No second OAuth.
      </p>
      <table className="table" style={{ marginBottom: 8 }}>
        <thead>
          <tr>
            <th>Account</th>
            <th>Type</th>
            <th>Status</th>
            <th style={{ width: 320 }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const linkedGl = it.linkedGlAccountId
              ? accounts.find((a) => a.id === it.linkedGlAccountId)
              : null;
            return (
              <tr key={it.plaidAccountId}>
                <td>
                  <div style={{ fontWeight: 500 }}>{it.name}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {it.mask ? `····${it.mask}` : it.plaidAccountId.slice(0, 8)}
                  </div>
                </td>
                <td className="muted" style={{ fontSize: 13 }}>
                  {[it.type, it.subtype].filter(Boolean).join(' / ') || '—'}
                </td>
                <td>
                  {it.isThisRow ? (
                    <span className="tag ok">This row (linked)</span>
                  ) : it.linkedBankAccountId ? (
                    <span className="tag">
                      Linked → {linkedGl ? `${linkedGl.code} ${linkedGl.name}` : 'another row'}
                    </span>
                  ) : (
                    <span className="muted" style={{ fontSize: 12 }}>Not linked</span>
                  )}
                </td>
                <td>
                  {it.isThisRow ? (
                    <span className="muted" style={{ fontSize: 12 }}>—</span>
                  ) : it.linkedBankAccountId ? (
                    <span className="muted" style={{ fontSize: 12 }}>Already linked elsewhere</span>
                  ) : (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button
                        type="button"
                        className="btn-secondary"
                        disabled={swap.isPending}
                        onClick={() => handleSwap(it)}
                        title="Replace the Plaid account this row points to (keeps the GL account)."
                      >
                        Swap this row to me
                      </button>
                      <span className="muted" style={{ fontSize: 11 }}>or</span>
                      <AccountPicker
                        id={`gl-${it.plaidAccountId}`}
                        accounts={accounts}
                        value={glChoice[it.plaidAccountId] ?? ''}
                        onChange={(id) => setGlChoice((p) => ({ ...p, [it.plaidAccountId]: id }))}
                        predicate={(a) =>
                          a.isActive &&
                          a.allowDirectPosting &&
                          (a.accountType === 'asset' || a.accountType === 'liability')
                        }
                        placeholder="— pick GL —"
                      />
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={!glChoice[it.plaidAccountId] || linkMore.isPending}
                        onClick={() => handleLinkAdditional(it)}
                        title="Create a new bank account row pointing at this Plaid account, with the GL account you pick."
                      >
                        Link as new
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {opError && <div className="field-error" style={{ fontSize: 13 }}>{opError}</div>}
      <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>
        Swap clears the sync cursor so the next Sync pulls fresh from the start for the new
        Plaid account. Link-as-new reuses the existing encrypted access token — no new OAuth.
      </p>
    </div>
  );
}

// Detect Plaid errors that require update-mode Link (user re-auth). Both
// ITEM_LOGIN_REQUIRED and PENDING_EXPIRATION are recoverable via the same flow.
// Match the code when it is stored, and Plaid's own wording when only the
// message was stored (every MSA row on 2026-10-07 carried just "the login
// details of this item have changed … use Link's update mode", and the button
// never appeared).
function needsUpdateMode(lastSyncError: string | null | undefined): boolean {
  if (!lastSyncError) return false;
  return (
    lastSyncError.includes('ITEM_LOGIN_REQUIRED') ||
    lastSyncError.includes('PENDING_EXPIRATION') ||
    /login details of this item have changed|user login is required|update mode/i.test(lastSyncError)
  );
}

// Pull the actionable message out of an API error. The transport-layer message
// is just "API 502"; the useful text (Plaid error_code + display_message) sits
// on body.error.
function extractApiError(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    return body?.error ?? `API ${err.status}`;
  }
  if (err instanceof Error) return err.message;
  return null;
}

/**
 * "Fix connection" button: opens Plaid Link in update mode against the
 * existing Item. On success, kicks off a normal sync so the user gets an
 * immediate green result. No new access_token is issued by Plaid — the
 * existing encrypted token becomes valid again.
 */
function FixConnectionButton({
  companyId,
  bankAccountId,
}: {
  companyId: string;
  bankAccountId: string;
}) {
  const createUpdateToken = useCreatePlaidUpdateLinkToken(companyId);
  const sync = useSyncBankAccount(companyId);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const openedRef = useRef(false);

  const onSuccess = useCallback(() => {
    // Plaid Link completed → existing access_token is valid again. Kick off
    // a normal sync so the user sees "N added" instead of a stale error.
    setLinkToken(null);
    openedRef.current = false;
    sync.mutate(bankAccountId);
  }, [sync, bankAccountId]);

  const onExit = useCallback((err: PlaidLinkError | null) => {
    setLinkToken(null);
    openedRef.current = false;
    if (err) {
      const parts = [err.error_code, err.error_type, err.display_message ?? err.error_message]
        .filter(Boolean);
      setError(parts.join(' · ') || 'Plaid Link aborted');
    }
  }, []);

  const { open, ready } = usePlaidLink({ token: linkToken, onSuccess, onExit });

  useEffect(() => {
    if (linkToken && ready && !openedRef.current) {
      openedRef.current = true;
      open();
    }
  }, [linkToken, ready, open]);

  const start = () => {
    setError(null);
    openedRef.current = false;
    // Same redirect back to /bank as the connect flow.
    const redirectUri = `${window.location.origin}/bank`;
    createUpdateToken.mutate(
      { bankAccountId, redirectUri },
      {
        onSuccess: (res) => setLinkToken(res.link_token),
        onError: (e) => setError(extractApiError(e) ?? 'Could not start Plaid update'),
      },
    );
  };

  const busy = createUpdateToken.isPending || sync.isPending || (!!linkToken && !ready);
  return (
    <>
      <button
        className="btn-primary"
        onClick={start}
        disabled={busy}
        style={{ marginRight: 6 }}
        title="Re-authenticate this bank connection to fix ITEM_LOGIN_REQUIRED."
      >
        {busy ? 'Opening…' : 'Fix connection'}
      </button>
      {error && (
        <div className="field-error" style={{ fontSize: 12, marginTop: 4 }}>
          {error}
        </div>
      )}
    </>
  );
}

function EditBankAccountForm({
  companyId,
  bank,
  onClose,
}: {
  companyId: string;
  bank: BankAccount;
  onClose: () => void;
}) {
  const update = useUpdateBankAccount(companyId);
  const { data: accounts = [] } = useAccounts(companyId);
  const cashAccounts = accounts.filter(
    (a) =>
      a.isActive &&
      a.allowDirectPosting &&
      (a.accountType === 'asset' || a.accountType === 'liability'),
  );

  const [form, setForm] = useState({
    glAccountId: bank.accountId,
    institutionName: bank.institutionName ?? '',
    accountName: bank.accountName,
    accountMask: bank.accountMask ?? '',
    accountType: bank.accountType as BankAccountType,
  });

  const errorMessage =
    update.error instanceof ApiError
      ? (update.error.body as { error?: string } | null)?.error ?? 'Request failed'
      : update.error?.message;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const input: UpdateBankAccountInput = {
          glAccountId: form.glAccountId,
          institutionName: form.institutionName.trim() || null,
          accountName: form.accountName.trim(),
          accountMask: form.accountMask.trim() || null,
          accountType: form.accountType,
        };
        update.mutate({ id: bank.id, input }, { onSuccess: () => onClose() });
      }}
    >
      <div style={{ fontWeight: 500, marginBottom: 8 }}>Edit bank account</div>
      <p className="muted" style={{ marginTop: 0, fontSize: 12 }}>
        Fix the GL account this feed posts to, or correct its name/type. Existing posted entries are
        unchanged; this affects how future matched transactions are booked.
      </p>
      <div className="form-grid">
        <label htmlFor={`e-gl-${bank.id}`}>GL account *</label>
        <AccountPicker
          id={`e-gl-${bank.id}`}
          accounts={accounts}
          value={form.glAccountId}
          onChange={(id) => setForm((p) => ({ ...p, glAccountId: id }))}
          predicate={(a) => a.isActive && a.allowDirectPosting && (a.accountType === 'asset' || a.accountType === 'liability')}
          placeholder={cashAccounts.length === 0 ? '(no eligible accounts)' : '— select —'}
          required
        />

        <label htmlFor={`e-inst-${bank.id}`}>Institution</label>
        <input
          id={`e-inst-${bank.id}`}
          value={form.institutionName}
          onChange={(e) => setForm((p) => ({ ...p, institutionName: e.target.value }))}
          placeholder="(optional)"
        />

        <label htmlFor={`e-name-${bank.id}`}>Account name *</label>
        <input
          id={`e-name-${bank.id}`}
          value={form.accountName}
          onChange={(e) => setForm((p) => ({ ...p, accountName: e.target.value }))}
          required
        />

        <label htmlFor={`e-mask-${bank.id}`}>Mask</label>
        <input
          id={`e-mask-${bank.id}`}
          value={form.accountMask}
          onChange={(e) => setForm((p) => ({ ...p, accountMask: e.target.value }))}
          maxLength={8}
          style={{ maxWidth: 120 }}
        />

        <label htmlFor={`e-type-${bank.id}`}>Type</label>
        <select
          id={`e-type-${bank.id}`}
          value={form.accountType}
          onChange={(e) => setForm((p) => ({ ...p, accountType: e.target.value as BankAccountType }))}
        >
          {BANK_ACCOUNT_TYPES.map((t) => (
            <option key={t} value={t}>{humanizeLabel(t)}</option>
          ))}
        </select>
      </div>

      {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          type="submit"
          className="btn-primary"
          disabled={update.isPending || !form.glAccountId || !form.accountName.trim()}
        >
          {update.isPending ? 'Saving…' : 'Save changes'}
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function CreateMockBankForm({
  companyId,
  onClose,
}: {
  companyId: string;
  onClose: () => void;
}) {
  const create = useCreateMockBankAccount(companyId);
  const { data: accounts = [] } = useAccounts(companyId);
  const cashAccounts = accounts.filter(
    (a) => a.isActive && a.allowDirectPosting && (a.accountType === 'asset' || a.accountType === 'liability'),
  );

  const [form, setForm] = useState({
    glAccountId: '',
    institutionName: 'Mock Bank',
    accountName: '',
    accountMask: '',
    accountType: 'depository' as BankAccountType,
  });

  // Once accounts arrive, default to the first eligible one if the user hasn't picked yet.
  useEffect(() => {
    if (!form.glAccountId && cashAccounts.length > 0) {
      setForm((p) => ({ ...p, glAccountId: cashAccounts[0]!.id }));
    }
  }, [cashAccounts, form.glAccountId]);

  const errorMessage =
    create.error instanceof ApiError
      ? (create.error.body as { error?: string } | null)?.error ?? 'Request failed'
      : create.error?.message;

  return (
    <form
      className="card"
      style={{ marginBottom: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        const input: CreateMockBankAccountInput = {
          glAccountId: form.glAccountId,
          institutionName: form.institutionName.trim(),
          accountName: form.accountName.trim(),
          accountType: form.accountType,
          accountMask: form.accountMask.trim() || undefined,
        };
        create.mutate(input, { onSuccess: () => onClose() });
      }}
    >
      <h2 style={{ marginTop: 0, fontSize: 18 }}>New mock bank account</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Mock accounts let you build and test bank workflows without real Plaid keys.
      </p>

      <div className="form-grid">
        <label htmlFor="gl">GL account *</label>
        <AccountPicker
          id="gl"
          accounts={accounts}
          value={form.glAccountId}
          onChange={(id) => setForm((p) => ({ ...p, glAccountId: id }))}
          predicate={(a) => a.isActive && a.allowDirectPosting && (a.accountType === 'asset' || a.accountType === 'liability')}
          placeholder={cashAccounts.length === 0 ? '(no eligible accounts)' : '— select —'}
          required
        />

        <label htmlFor="inst">Institution</label>
        <input
          id="inst"
          value={form.institutionName}
          onChange={(e) => setForm((p) => ({ ...p, institutionName: e.target.value }))}
          required
        />

        <label htmlFor="acctname">Account name *</label>
        <input
          id="acctname"
          value={form.accountName}
          onChange={(e) => setForm((p) => ({ ...p, accountName: e.target.value }))}
          placeholder="Operating Checking"
          required
        />

        <label htmlFor="mask">Mask</label>
        <input
          id="mask"
          value={form.accountMask}
          onChange={(e) => setForm((p) => ({ ...p, accountMask: e.target.value }))}
          placeholder="1234"
          maxLength={8}
          style={{ maxWidth: 120 }}
        />

        <label htmlFor="type">Type</label>
        <select
          id="type"
          value={form.accountType}
          onChange={(e) => setForm((p) => ({ ...p, accountType: e.target.value as BankAccountType }))}
        >
          {BANK_ACCOUNT_TYPES.map((t) => (
            <option key={t} value={t}>{humanizeLabel(t)}</option>
          ))}
        </select>
      </div>

      {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button
          type="submit"
          className="btn-primary"
          disabled={create.isPending || !form.glAccountId || !form.accountName.trim()}
        >
          {create.isPending ? 'Creating…' : 'Create mock account'}
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function CreateManualBankForm({ companyId, onClose }: { companyId: string; onClose: () => void }) {
  const create = useCreateManualBankAccount(companyId);
  const { data: accounts = [] } = useAccounts(companyId);
  const cashAccounts = accounts.filter(
    (a) => a.isActive && a.allowDirectPosting && (a.accountType === 'asset' || a.accountType === 'liability'),
  );

  const [form, setForm] = useState({
    glAccountId: '',
    institutionName: '',
    accountName: '',
    accountMask: '',
    accountType: 'depository' as BankAccountType,
  });
  useEffect(() => {
    if (!form.glAccountId && cashAccounts.length > 0) {
      setForm((p) => ({ ...p, glAccountId: cashAccounts[0]!.id }));
    }
  }, [cashAccounts, form.glAccountId]);

  const errorMessage =
    create.error instanceof ApiError
      ? (create.error.body as { error?: string } | null)?.error ?? 'Request failed'
      : create.error?.message;

  return (
    <form
      className="card"
      style={{ marginBottom: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        const input: CreateManualBankAccountInput = {
          glAccountId: form.glAccountId,
          institutionName: form.institutionName.trim() || undefined,
          accountName: form.accountName.trim(),
          accountType: form.accountType,
          accountMask: form.accountMask.trim() || undefined,
        };
        create.mutate(input, { onSuccess: () => onClose() });
      }}
    >
      <h2 style={{ marginTop: 0, fontSize: 18 }}>New manual bank account</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        No Plaid — key in the few transactions each month yourself. They appear in the feed ready
        to categorize and reconcile.
      </p>

      <div className="form-grid">
        <label htmlFor="m-gl">GL account *</label>
        <select
          id="m-gl"
          value={form.glAccountId}
          onChange={(e) => setForm((p) => ({ ...p, glAccountId: e.target.value }))}
          required
        >
          <option value="">{cashAccounts.length === 0 ? '(no eligible accounts)' : '— select —'}</option>
          {cashAccounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.code} — {a.name} ({humanizeLabel(a.accountType)})
            </option>
          ))}
        </select>

        <label htmlFor="m-inst">Institution</label>
        <input
          id="m-inst"
          value={form.institutionName}
          onChange={(e) => setForm((p) => ({ ...p, institutionName: e.target.value }))}
          placeholder="(optional)"
        />

        <label htmlFor="m-name">Account name *</label>
        <input
          id="m-name"
          value={form.accountName}
          onChange={(e) => setForm((p) => ({ ...p, accountName: e.target.value }))}
          placeholder="Operating Checking"
          required
        />

        <label htmlFor="m-mask">Mask</label>
        <input
          id="m-mask"
          value={form.accountMask}
          onChange={(e) => setForm((p) => ({ ...p, accountMask: e.target.value }))}
          placeholder="1234"
          maxLength={8}
          style={{ maxWidth: 120 }}
        />

        <label htmlFor="m-type">Type</label>
        <select
          id="m-type"
          value={form.accountType}
          onChange={(e) => setForm((p) => ({ ...p, accountType: e.target.value as BankAccountType }))}
        >
          {BANK_ACCOUNT_TYPES.map((t) => (
            <option key={t} value={t}>{humanizeLabel(t)}</option>
          ))}
        </select>
      </div>

      {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button
          type="submit"
          className="btn-primary"
          disabled={create.isPending || !form.glAccountId || !form.accountName.trim()}
        >
          {create.isPending ? 'Creating…' : 'Create manual account'}
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}

const PLAID_BANK_TYPES = new Set<string>(BANK_ACCOUNT_TYPES);
function mapBankType(t?: string | null): BankAccountType {
  return t && PLAID_BANK_TYPES.has(t) ? (t as BankAccountType) : 'depository';
}

function ConnectPlaidForm({ companyId, onClose }: { companyId: string; onClose: () => void }) {
  const { data: accounts = [] } = useAccounts(companyId);
  const cashAccounts = accounts.filter(
    (a) => a.isActive && a.allowDirectPosting && (a.accountType === 'asset' || a.accountType === 'liability'),
  );
  const [glAccountId, setGlAccountId] = useState('');
  useEffect(() => {
    if (!glAccountId && cashAccounts.length > 0) setGlAccountId(cashAccounts[0]!.id);
  }, [cashAccounts, glAccountId]);

  const createToken = useCreatePlaidLinkToken(companyId);
  const exchange = useExchangePlaidToken(companyId);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  // Plaid's own error if Link UI aborts (institution not OAuth-enabled,
  // redirect_uri mismatch, etc.) — without this, the modal just "fades" and
  // we have no way to know why.
  const [plaidError, setPlaidError] = useState<string | null>(null);
  const openedRef = useRef(false);

  const onSuccess = useCallback(
    (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      const acct = metadata.accounts?.[0];
      exchange.mutate(
        {
          publicToken,
          glAccountId,
          plaidAccountId: acct?.id ?? '',
          institutionName: metadata.institution?.name ?? undefined,
          accountName: acct?.name ?? undefined,
          accountMask: acct?.mask ?? undefined,
          accountType: mapBankType(acct?.type),
          accountSubtype: acct?.subtype ?? undefined,
        },
        { onSuccess: () => onClose() },
      );
    },
    [glAccountId, exchange, onClose],
  );

  const onExit = useCallback(
    (err: PlaidLinkError | null, metadata: PlaidLinkOnExitMetadata) => {
      // Reset so a retry can reopen the modal.
      openedRef.current = false;
      setLinkToken(null);
      if (err) {
        // eslint-disable-next-line no-console
        console.error('[plaid-link] exit with error', { err, metadata });
        const parts = [err.error_code, err.error_type, err.display_message ?? err.error_message]
          .filter(Boolean);
        setPlaidError(parts.join(' · ') || 'Plaid Link aborted (no error code returned)');
      } else if (metadata.status && metadata.status !== 'requires_account_selection') {
        // User-initiated exits leave err=null but status carries a reason.
        // eslint-disable-next-line no-console
        console.warn('[plaid-link] exit without error', { metadata });
      }
    },
    [],
  );

  const { open, ready } = usePlaidLink({ token: linkToken, onSuccess, onExit });

  // Once we have a link token and Plaid Link is ready, open it once.
  useEffect(() => {
    if (linkToken && ready && !openedRef.current) {
      openedRef.current = true;
      open();
    }
  }, [linkToken, ready, open]);

  const startConnect = () => {
    openedRef.current = false;
    setPlaidError(null);
    // For OAuth-required banks (every major US bank in Production), Plaid Link
    // redirects the whole page to the bank's auth screen and back to this URL.
    // We persist the link_token + form state in sessionStorage so the resume
    // handler at the top of <BankPage/> can finish the flow on return.
    const redirectUri = `${window.location.origin}/bank`;
    createToken.mutate(
      { redirectUri },
      {
        onSuccess: (res) => {
          sessionStorage.setItem(
            PLAID_RESUME_KEY,
            JSON.stringify({
              companyId,
              glAccountId,
              token: res.link_token,
              savedAt: Date.now(),
            }),
          );
          setLinkToken(res.link_token);
        },
      },
    );
  };

  const errorMessage =
    createToken.error instanceof ApiError
      ? (createToken.error.body as { error?: string } | null)?.error ?? 'Could not start Plaid'
      : exchange.error instanceof ApiError
        ? (exchange.error.body as { error?: string } | null)?.error ?? 'Could not link account'
        : createToken.error?.message ?? exchange.error?.message;

  const busy = createToken.isPending || exchange.isPending || (!!linkToken && !ready);

  return (
    <form className="card" style={{ marginBottom: 16 }} onSubmit={(e) => e.preventDefault()}>
      <h2 style={{ marginTop: 0, fontSize: 18 }}>Connect a bank (Plaid)</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Pick the GL account this bank ties to, then connect. In Sandbox, log in with{' '}
        <code>user_good</code> / <code>pass_good</code>.
      </p>
      <div className="form-grid">
        <label htmlFor="p-gl">GL account *</label>
        <select id="p-gl" value={glAccountId} onChange={(e) => setGlAccountId(e.target.value)} required>
          <option value="">{cashAccounts.length === 0 ? '(no eligible accounts)' : '— select —'}</option>
          {cashAccounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.code} — {a.name} ({humanizeLabel(a.accountType)})
            </option>
          ))}
        </select>
      </div>

      {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}
      {plaidError && (
        <div className="field-error" style={{ marginTop: 12 }}>
          <strong>Plaid Link aborted:</strong> {plaidError}
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Common causes: redirect URI not registered in Plaid Dashboard for this environment,
            institution not OAuth-enabled for your Production account, or the link_token expired.
            Check the browser console for the full Plaid error object.
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button type="button" className="btn-primary" disabled={!glAccountId || busy} onClick={startConnect}>
          {busy ? 'Connecting…' : 'Connect bank'}
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// Resumes Plaid Link after an OAuth bank (Chase, BofA, Wells, etc.) bounces the
// user back to /bank?oauth_state_id=... Reads the link_token + glAccountId we
// stashed in sessionStorage before launch, reopens Plaid Link with
// receivedRedirectUri set to the current URL, and finishes the exchange on
// success. If the storage is empty (e.g. user opened the URL directly in a new
// tab) we just clean the URL and bail — the user can click Connect again.
function PlaidOAuthResume({ companyId }: { companyId: string }) {
  const exchange = useExchangePlaidToken(companyId);
  const [resumed, setResumed] = useState(false);
  const openedRef = useRef(false);
  const savedRaw = typeof window !== 'undefined' ? sessionStorage.getItem(PLAID_RESUME_KEY) : null;
  const saved = (() => {
    if (!savedRaw) return null;
    try {
      const parsed = JSON.parse(savedRaw) as { companyId: string; glAccountId: string; token: string; savedAt: number };
      // Plaid link_tokens are valid ~4 hours; bail on anything stale.
      if (Date.now() - parsed.savedAt > 4 * 60 * 60 * 1000) return null;
      if (parsed.companyId !== companyId) return null;
      return parsed;
    } catch {
      return null;
    }
  })();

  const cleanup = useCallback(() => {
    sessionStorage.removeItem(PLAID_RESUME_KEY);
    window.history.replaceState({}, '', '/bank');
    setResumed(true);
  }, []);

  const onSuccess = useCallback(
    (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      if (!saved) return;
      const acct = metadata.accounts?.[0];
      exchange.mutate(
        {
          publicToken,
          glAccountId: saved.glAccountId,
          plaidAccountId: acct?.id ?? '',
          institutionName: metadata.institution?.name ?? undefined,
          accountName: acct?.name ?? undefined,
          accountMask: acct?.mask ?? undefined,
          accountType: mapBankType(acct?.type),
          accountSubtype: acct?.subtype ?? undefined,
        },
        { onSuccess: cleanup, onError: cleanup },
      );
    },
    [saved, exchange, cleanup],
  );

  const { open, ready } = usePlaidLink({
    token: saved?.token ?? null,
    receivedRedirectUri: typeof window !== 'undefined' ? window.location.href : undefined,
    onSuccess,
    onExit: cleanup,
  });

  useEffect(() => {
    if (!saved && !resumed) {
      // Nothing to resume — clean the URL so a refresh doesn't loop.
      cleanup();
      return;
    }
    if (saved && ready && !openedRef.current) {
      openedRef.current = true;
      open();
    }
  }, [saved, ready, open, resumed, cleanup]);

  if (resumed) return null;
  return (
    <div className="card" style={{ marginBottom: 16, padding: 16 }}>
      <strong>Finishing bank connection…</strong>
      <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
        Returning from your bank's sign-in.
      </p>
    </div>
  );
}

function AddManualTransactionForm({
  companyId,
  bankAccountId,
  onClose,
}: {
  companyId: string;
  bankAccountId: string;
  onClose: () => void;
}) {
  const add = useAddManualTransaction(companyId);
  const [date, setDate] = useState(todayIso());
  const [direction, setDirection] = useState<ManualTxDirection>('out');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [merchantName, setMerchantName] = useState('');

  const errorMessage =
    add.error instanceof ApiError
      ? (add.error.body as { error?: string } | null)?.error ?? 'Request failed'
      : add.error?.message;

  const valid = !!date && !!description.trim() && parseFloat(amount) > 0;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        add.mutate(
          {
            bankAccountId,
            input: {
              date,
              direction,
              amount: String(parseFloat(amount)),
              description: description.trim(),
              merchantName: merchantName.trim() || undefined,
            },
          },
          {
            onSuccess: () => {
              // Keep the form open for fast multi-entry; just clear the line.
              setAmount('');
              setDescription('');
              setMerchantName('');
            },
          },
        );
      }}
      style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}
    >
      <label style={{ fontSize: 12 }}>
        Date<br />
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
      </label>
      <label style={{ fontSize: 12 }}>
        Direction<br />
        <select value={direction} onChange={(e) => setDirection(e.target.value as ManualTxDirection)}>
          {MANUAL_TX_DIRECTIONS.map((d) => (
            <option key={d} value={d}>{d === 'out' ? 'Money out' : 'Money in'}</option>
          ))}
        </select>
      </label>
      <label style={{ fontSize: 12 }}>
        Amount<br />
        <input
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
          style={{ width: 110, textAlign: 'right' }}
        />
      </label>
      <label style={{ fontSize: 12, flex: 1, minWidth: 180 }}>
        Description<br />
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. Office Depot"
          style={{ width: '100%' }}
          required
        />
      </label>
      <label style={{ fontSize: 12, minWidth: 140 }}>
        Merchant<br />
        <input value={merchantName} onChange={(e) => setMerchantName(e.target.value)} placeholder="(optional)" style={{ width: '100%' }} />
      </label>
      <button type="submit" className="btn-primary" disabled={!valid || add.isPending}>
        {add.isPending ? 'Adding…' : 'Add'}
      </button>
      <button type="button" className="btn-secondary" onClick={onClose}>Done</button>
      {errorMessage && <div className="field-error" style={{ width: '100%' }}>{errorMessage}</div>}
      {add.isSuccess && !errorMessage && (
        <div className="muted" style={{ width: '100%', fontSize: 12 }}>
          Added — appears in the transaction feed, ready to categorize.
        </div>
      )}
    </form>
  );
}

interface SeedRow {
  date: string;
  amount: string;
  description: string;
  merchantName: string;
}

function SeedTransactionsForm({
  companyId,
  bankAccountId,
  onClose,
}: {
  companyId: string;
  bankAccountId: string;
  onClose: () => void;
}) {
  const seed = useSeedMockTransactions(companyId);
  const [rows, setRows] = useState<SeedRow[]>(() => [
    { date: todayIso(), amount: '42.55', description: 'STARBUCKS', merchantName: 'Starbucks' },
  ]);

  const addRow = () =>
    setRows((p) => [
      ...p,
      { date: todayIso(), amount: '', description: '', merchantName: '' },
    ]);
  const removeRow = (i: number) =>
    setRows((p) => p.filter((_, idx) => idx !== i));
  const updateRow = (i: number, patch: Partial<SeedRow>) =>
    setRows((p) => p.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const errorMessage =
    seed.error instanceof ApiError
      ? (seed.error.body as { error?: string } | null)?.error ?? 'Request failed'
      : seed.error?.message;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const valid = rows
          .filter((r) => r.description.trim() && r.amount.trim())
          .map((r) => ({
            date: r.date,
            amount: parseFloat(r.amount),
            description: r.description.trim(),
            merchantName: r.merchantName.trim() || null,
          }));
        if (valid.length === 0) return;
        seed.mutate(
          { bankAccountId, txns: valid },
          {
            onSuccess: () => onClose(),
          },
        );
      }}
    >
      <div style={{ fontWeight: 500, marginBottom: 8 }}>Seed mock transactions</div>
      <p className="muted" style={{ marginTop: 0, fontSize: 12 }}>
        Positive amount = money leaving the account (payment/charge). Negative = money entering (deposit).
        Rules run automatically after seeding.
      </p>
      <table className="table" style={{ marginBottom: 8 }}>
        <thead>
          <tr>
            <th style={{ width: 140 }}>Date</th>
            <th style={{ width: 120 }}>Amount</th>
            <th>Description</th>
            <th>Merchant</th>
            <th style={{ width: 50 }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>
                <input
                  type="date"
                  value={r.date}
                  onChange={(e) => updateRow(i, { date: e.target.value })}
                  required
                />
              </td>
              <td>
                <input
                  type="text"
                  inputMode="decimal"
                  value={r.amount}
                  onChange={(e) => updateRow(i, { amount: e.target.value })}
                  placeholder="42.55"
                  style={{ width: '100%', textAlign: 'right' }}
                  required
                />
              </td>
              <td>
                <input
                  type="text"
                  value={r.description}
                  onChange={(e) => updateRow(i, { description: e.target.value })}
                  style={{ width: '100%' }}
                  required
                />
              </td>
              <td>
                <input
                  type="text"
                  value={r.merchantName}
                  onChange={(e) => updateRow(i, { merchantName: e.target.value })}
                  style={{ width: '100%' }}
                />
              </td>
              <td>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={() => removeRow(i)}
                  disabled={rows.length <= 1}
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" className="btn-secondary" onClick={addRow}>
        + Add row
      </button>
      {errorMessage && <div className="field-error" style={{ marginTop: 8 }}>{errorMessage}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="submit" className="btn-primary" disabled={seed.isPending}>
          {seed.isPending ? 'Seeding…' : `Seed ${rows.filter((r) => r.description.trim()).length} transactions`}
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// Suppress unused-import linter warnings if formatAccountingDate isn't used elsewhere.
void formatAccountingDate;
